var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useCallback, useEffect, useMemo, useRef, useReducer } from "react";
var SearchStatus;
(function (SearchStatus) {
    SearchStatus["Idle"] = "Idle";
    SearchStatus["Running"] = "Running";
    SearchStatus["LongRunning"] = "LongRunning";
})(SearchStatus || (SearchStatus = {}));
var isEnabled = function (_a) {
    var _b, _c, _d;
    var setIsLongRunning = _a.setIsLongRunning, detectLongRunningQueries = _a.detectLongRunningQueries;
    var enabled = !!setIsLongRunning && !!detectLongRunningQueries && (detectLongRunningQueries === null || detectLongRunningQueries === void 0 ? void 0 : detectLongRunningQueries.timeout) > 0;
    return {
        count: enabled && !!((_b = detectLongRunningQueries === null || detectLongRunningQueries === void 0 ? void 0 : detectLongRunningQueries.queries) === null || _b === void 0 ? void 0 : _b.includes("count")),
        search: enabled && !!((_c = detectLongRunningQueries === null || detectLongRunningQueries === void 0 ? void 0 : detectLongRunningQueries.queries) === null || _c === void 0 ? void 0 : _c.includes("search")),
        data: enabled && !!((_d = detectLongRunningQueries === null || detectLongRunningQueries === void 0 ? void 0 : detectLongRunningQueries.queries) === null || _d === void 0 ? void 0 : _d.includes("data")),
    };
};
var defaultState = function (props) { return ({
    status: {
        count: SearchStatus.Idle,
        search: SearchStatus.Idle,
        data: SearchStatus.Idle,
    },
    isLongRunning: false,
    enabled: isEnabled(props),
}); };
var getIsLongRunning = function (_a) {
    var search = _a.search, count = _a.count, data = _a.data;
    return search === SearchStatus.LongRunning || count === SearchStatus.LongRunning || data === SearchStatus.LongRunning;
};
var reducer = function (state, action) {
    var _a;
    if (!state.enabled[action.type]) {
        return state;
    }
    var previousStatus = state.status[action.type];
    var newStatus = action.status;
    if (previousStatus === newStatus) {
        return state;
    }
    if (previousStatus === SearchStatus.Idle && newStatus === SearchStatus.LongRunning) {
        //If the endpoint has already completed, ignore...
        return state;
    }
    var newStatuses = __assign(__assign({}, state.status), (_a = {}, _a[action.type] = newStatus, _a));
    var isLongRunning = getIsLongRunning(newStatuses);
    return __assign(__assign({}, state), { status: newStatuses, isLongRunning: isLongRunning });
};
var startTimeout = function (timeoutRef, timeout) { return function (cb) {
    if (!timeout) {
        return;
    }
    if (timeoutRef) {
        clearTimeout(timeoutRef);
    }
    timeoutRef = setTimeout(cb, timeout);
}; };
var stopTimeout = function (timeoutRef) {
    if (timeoutRef) {
        clearTimeout(timeoutRef);
    }
    timeoutRef = undefined;
};
var stopTimeouts = function (refs) {
    stopTimeout(refs === null || refs === void 0 ? void 0 : refs.search);
    stopTimeout(refs === null || refs === void 0 ? void 0 : refs.count);
    stopTimeout(refs === null || refs === void 0 ? void 0 : refs.data);
};
export function useLongRunningFetcher(_a) {
    var setIsLongRunning = _a.setIsLongRunning, detectLongRunningQueries = _a.detectLongRunningQueries;
    var timeoutsRefs = useRef({ count: undefined, search: undefined, data: undefined });
    var _b = useReducer(reducer, defaultState({ setIsLongRunning: setIsLongRunning, detectLongRunningQueries: detectLongRunningQueries })), state = _b[0], dispatch = _b[1];
    var started = useCallback(function (type) {
        var _a;
        if (!state.enabled[type]) {
            return;
        }
        dispatch({ status: SearchStatus.Running, type: type });
        startTimeout((_a = timeoutsRefs === null || timeoutsRefs === void 0 ? void 0 : timeoutsRefs.current) === null || _a === void 0 ? void 0 : _a[type], detectLongRunningQueries === null || detectLongRunningQueries === void 0 ? void 0 : detectLongRunningQueries.timeout)(function () {
            dispatch({ status: SearchStatus.LongRunning, type: type });
        });
    }, [detectLongRunningQueries === null || detectLongRunningQueries === void 0 ? void 0 : detectLongRunningQueries.timeout, state.enabled]);
    var completed = useCallback(function (type) {
        dispatch({ status: SearchStatus.Idle, type: type });
    }, []);
    useEffect(function () {
        setIsLongRunning === null || setIsLongRunning === void 0 ? void 0 : setIsLongRunning(state.isLongRunning);
    }, [setIsLongRunning, state.isLongRunning]);
    useEffect(function () {
        var refs = timeoutsRefs === null || timeoutsRefs === void 0 ? void 0 : timeoutsRefs.current;
        // Do nothing on mount. On unmount, clearTimeout.
        return function () {
            stopTimeouts(refs);
        };
    }, []);
    return useMemo(function () { return ({
        started: started,
        completed: completed,
    }); }, [completed, started]);
}
//# sourceMappingURL=use-long-running-fetchers.js.map